import React, { useState, useCallback, useEffect } from 'react';
import styles from './index.modules.scss';
import { Image, Radio, Tag, Text } from '@shakkerai/pro-components';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import { Icon } from '@shakkerai/components';
import { translate } from '@shakkerai/util-i18n';
import { setSharedData } from '@starflow/app/store';
import { useGenHistoryItemEventHooks } from '../../hooks/eventHooks.js';
import {
  useRightMenuState,
  useViewDetailState,
  useNSFWStore,
  useComfirmModalState,
  useSelectState,
} from '../../store/base.js';
import { eventBus } from '@starflow/app/helpers';
import { ImageShareButton } from '@shakkerai/pro-components';
import cs from 'classnames';

const ImageItem = (props) => {
  const { item, onImageClick, onDeleteFinish } = props;

  const src = convert2CompressedUrl(item.imageId, 200);

  const [sharePopoverOpened] = useState(false);

  const [collected, setCollected] = useState(item.groupId > 0);

  const { addViewItem, isNSFWView } = useNSFWStore();

  const { seleteImage, isChecked, isSelectMode } = useSelectState(item);

  const { onCollect, onDelete, onDownload, onCancelCollect } = useGenHistoryItemEventHooks({
    onCollectDone() {
      setCollected(!collected);
    },
    onDeleteDone() {
      onDeleteFinish();
    },
    itemInfo: item,
    source: 'history',
  });

  useEffect(() => {
    setCollected(item.groupId > 0);
  }, [item.groupId]);

  const { showComfirmModal } = useComfirmModalState();

  const onDeleteInner = () => {
    showComfirmModal({
      content: translate('history_deletesingleimage'),
      subContent: translate('history_deletecheck_sub'),
      onConfirmCallback: onDelete,
    });
  };

  const { showRightMenu } = useRightMenuState();

  const handleDragStart = () => {
    const { generateId, outputId, width, height, imageId: url } = item;
    setSharedData('dragging-element', {
      source: 'history',
      innerTaskId: generateId,
      generateId,
      outputId,
      width,
      height,
      url,
    });
  };

  const onRightClick = (event) => {
    event.preventDefault();

    showRightMenu(item, {
      top: event.pageY,
      left: event.pageX,
    });
  };

  const onItemClick = useCallback(() => {
    // 如果是选择模式，则点击图片为选中或取消选中，否则的话就是查看图片
    if (isSelectMode) {
      seleteImage();
    } else if (sharePopoverOpened) {
      console.info(`分享开启模式，点击不做处理`);
    } else {
      onImageClick();
    }
  }, [onImageClick, isSelectMode, seleteImage, sharePopoverOpened]);

  return (
    <div
      className={`${styles.imageC} ${isNSFWView(item) ? null : styles.imageCAlias}`}
      onContextMenu={onRightClick}
      data-image-item
    >
      <Image onDragStart={handleDragStart} src={src} onClick={onItemClick} className={styles.image} draggable />

      <div className={`${styles.hoverC} ${isSelectMode || sharePopoverOpened ? styles.hoverVisual : null}`}>
        <div className={styles.backgroundC}></div>

        <div className={styles.head}>
          <Radio className={`h-4 w-4 ${styles.radio}`} checked={isChecked} onClick={seleteImage} />

          <div className="flex row gap-1">
            {!isSelectMode && (
              <div
                onClick={(event) => {
                  event.stopPropagation();
                  onDeleteInner();
                }}
                className={styles.icon}
              >
                <Icon class={'icon-prodelet'} fontSize={'16px'} color={'#FFFFFF'} />
              </div>
            )}
            {!isSelectMode && (
              <ImageShareButton
                imageInfo={{
                  innerTaskId: item.generateId,
                  innerImageId: item.outputId,
                }}
              >
                <div className={styles.icon}>
                  <Icon class={'icon-proshare'} fontSize={'16px'} color={'#FFFFFF'} />
                </div>
              </ImageShareButton>
            )}
            {!isSelectMode && (
              <div
                onClick={(event) => {
                  event.stopPropagation();
                  onDownload();
                }}
                className={styles.icon}
              >
                <Icon class={'icon-prodownloa'} fontSize={'16px'} color={'#FFFFFF'} />
              </div>
            )}
            <div
              onClick={(event) => {
                event.stopPropagation();
                if (collected) {
                  onCancelCollect();
                } else {
                  onCollect();
                }
              }}
              className={styles.icon}
            >
              <Icon class={'icon-procollect'} fontSize={'16px'} color={collected ? '#FFC351' : '#FFFFFF'} />
            </div>
          </div>
        </div>
      </div>

      {collected && (
        <Icon
          class={'icon-procollect absolute right-2 top-2'}
          fontSize={'16px'}
          color={collected ? '#FFC351' : '#FFFFFF'}
        />
      )}

      {isNSFWView(item) && (
        <div className={styles.nsfw}>
          <Text iconClass="icon-border_generate_info" color="white">
            NSFW Content
          </Text>
          <div className={styles.viewButton} data-role="gen-history-trigger" onClick={() => addViewItem(item)}>
            View
          </div>
        </div>
      )}
    </div>
  );
};

export default (props) => {
  const { itemInfo, deleteFreshLabel, source } = props;

  const { viewImage } = useViewDetailState();

  const onDeleteFinish = useCallback(
    (index) => {
      eventBus.emit(deleteFreshLabel, itemInfo, index);
    },
    [itemInfo, deleteFreshLabel],
  );

  return itemInfo.images ? (
    <div className={styles.historyItemC}>
      {itemInfo?.generateType === 19 ? (
        <div className={cs(styles.head, 'flex justify-between')}>
          <div className="rounded-sm border-[1px] border-[#2ce4c5] bg-[#EAFCF9] text-[#2CE4C5] font-normal text-xs px-2 py-[1px]">
            {translate('flow_g_apply')}
          </div>
          <div className={styles.time}>{itemInfo.createTime}</div>
        </div>
      ) : (
        <div className={styles.head}>
          {itemInfo.inpaint ? (
            <Tag className={styles.tag} iconClass="icon-proinpaint" color="var(--color-primary-font)" type="warn">
              {translate('imagetool_inpaint')}
            </Tag>
          ) : (
            <Icon class={'icon-proimages leading-4'} fontSize={'16px'} color="var(--color-subsidiary-font)" />
          )}
          <div className={styles.prompt}>{itemInfo.param?.prompt}</div>
          <div className={styles.time}>{itemInfo.createTime}</div>
        </div>
      )}

      <div className={styles.imagesC}>
        {itemInfo.images?.map((item, index) => (
          <ImageItem
            item={item}
            index={index}
            key={item.outputId}
            onDeleteFinish={() => onDeleteFinish(index)}
            onImageClick={() => viewImage(itemInfo, index, source)}
          />
        ))}
      </div>
    </div>
  ) : null;
};
